import { Sun, Moon, Flame, Leaf, Snowflake, Image as ImageIcon } from "lucide-react";

export const THEME_OPTIONS = [
  { key: "light", group: "Default", label: "Light", Icon: Sun, hint: "The original portal look, bright" },
  { key: "dark", group: "Default", label: "Dark", Icon: Moon, hint: "The original portal look, low-light" },
  { key: "orange", group: "Themes", label: "Orange", Icon: Flame, hint: "Ember neumorphic — soft raised cards, round menus, bottom dock on phones" },
  { key: "green", group: "Themes", label: "Green", Icon: Leaf, hint: "Neon on black — floating dock nav on every screen, glowing accents" },
  { key: "blue", group: "Themes", label: "Blue", Icon: Snowflake, hint: "Glacier glass — side-rail navigation on desktop, frosted cards" },
  { key: "graphic", group: "Themes", label: "Graphic", Icon: ImageIcon, hint: "Trade photography — duct & pipe insulation scenes behind frosted glass, different per section (each workgroup gets its own set)" },
];

export const themeLabel = (k) => {
  const o = THEME_OPTIONS.find((x) => x.key === k);
  return !o ? k : o.group === "Default" ? `Default ${o.label}` : o.label;
};

const PALETTE = {
  light: { bg: "#f4f5f7", card: "#ffffff", accent: "#1a3fd1", radius: 4, shadow: "0 0 0 1px #e3e5ea", nav: "top" },
  dark: { bg: "#050505", card: "#101010", accent: "#1a3fd1", radius: 4, shadow: "0 0 0 1px #262626", nav: "top" },
  orange: { bg: "#17181d", card: "#1e1f26", accent: "linear-gradient(135deg,#ffb347,#ff5e1f)", radius: 10, shadow: "3px 3px 6px #0e0f12, -2px -2px 5px #2a2c35", nav: "bottom" },
  green: { bg: "#0a0d0b", card: "#121715", accent: "linear-gradient(135deg,#5dffb0,#19d873)", radius: 8, shadow: "0 0 0 1px rgba(57,255,154,.25)", nav: "bottom" },
  blue: { bg: "linear-gradient(135deg,#0a1020,#13224a)", card: "rgba(140,170,255,.14)", accent: "linear-gradient(135deg,#6aa0ff,#22d3ee)", radius: 7, shadow: "0 0 0 1px rgba(130,165,255,.3)", nav: "side" },
  graphic: { bg: "url(https://images.unsplash.com/photo-1707596830261-9c6138a6dd3b?crop=entropy&cs=srgb&fm=jpg&q=40&w=400) center/cover", card: "rgba(16,17,22,.7)", accent: "linear-gradient(135deg,#f2f6fa,#aebccb)", radius: 9, shadow: "0 0 0 1px rgba(255,255,255,.15)", nav: "bottom" },
};

/** Swatch preview — a tiny mock of the layout each theme uses. */
const Swatch = ({ theme }) => {
  const p = PALETTE[theme];
  const pill = theme !== "light" && theme !== "dark";
  const Nav = () => <div className="flex gap-1" style={{ background: p.card, borderRadius: pill ? 999 : 3, boxShadow: p.shadow, padding: 3 }}>{[0, 1, 2].map((i) => <span key={i} className="h-2 flex-1" style={{ background: i === 0 ? p.accent : "transparent", borderRadius: 999 }} />)}</div>;
  return (
    <div className="flex h-20 w-full flex-col gap-1.5 overflow-hidden rounded-lg p-2" style={{ background: p.bg }} aria-hidden="true">
      <div className="flex items-center justify-between" style={{ background: p.card, borderRadius: p.radius, boxShadow: p.shadow, padding: 4 }}>
        <span className="h-2 w-8 rounded-full" style={{ background: p.accent }} />
        <span className="flex gap-1">{[0, 1].map((i) => <span key={i} className="h-3 w-3" style={{ background: i === 0 ? p.accent : p.card, borderRadius: pill ? 999 : 3, boxShadow: p.shadow }} />)}</span>
      </div>
      {p.nav === "top" && <Nav />}
      <div className={`flex flex-1 gap-1.5 ${p.nav === "side" ? "" : ""}`}>
        {p.nav === "side" && <div className="w-5" style={{ background: p.card, borderRadius: p.radius, boxShadow: p.shadow }} />}
        {[0, 1].map((i) => <div key={i} className="flex-1" style={{ background: p.card, borderRadius: p.radius, boxShadow: p.shadow }} />)}
      </div>
      {p.nav === "bottom" && <Nav />}
    </div>
  );
};

/**
 * ThemePicker — Default (Light / Dark) and the four full re-skins.
 * `value` is the selected key; `onPick(key)`.
 */
const ThemePicker = ({ value, onPick, testPrefix = "theme-option" }) => (
  <div className="space-y-4" data-testid="theme-options">
    {["Default", "Themes"].map((group) => (
      <div key={group}>
        <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{group}</p>
        <div className="grid grid-cols-2 gap-3">
          {THEME_OPTIONS.filter((o) => o.group === group).map(({ key, label, Icon, hint }) => {
            const active = value === key;
            return (
              <button key={key} type="button" onClick={() => onPick(key)} data-testid={`${testPrefix}-${key}`} aria-pressed={active}
                className={`flex flex-col items-start gap-2 rounded-xl border p-3 text-left transition-colors duration-300 ${active ? "border-[#1a3fd1] bg-[#1a3fd1]/15 text-white" : "border-white/10 bg-white/[0.03] text-white/70 hover:border-white/25 hover:text-white"}`}>
                <Swatch theme={key} />
                <span className="flex items-center gap-2 text-sm font-semibold"><Icon className={`h-4 w-4 ${active ? "text-[#8fa4f0]" : ""}`} />{label}</span>
                <span className="text-xs text-white/40">{hint}</span>
              </button>
            );
          })}
        </div>
      </div>
    ))}
  </div>
);

export default ThemePicker;
